import React, { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Check, HelpCircle } from 'lucide-react';
import { servicesData } from '../data/servicesData';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';

const ServiceDetailPage = ({ serviceKey }) => {
  const service = servicesData[serviceKey];

  const openLeadForm = () => {
    window.dispatchEvent(new CustomEvent('openLeadForm'));
  };

  if (!service) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-slate-900 mb-4">Service Not Found</h1>
          <Link to="/" className="text-amber-700 hover:text-amber-800 font-medium">
            Return to Homepage
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" data-testid="service-detail-page">
      {/* Hero Section */}
      <section className="relative bg-slate-900 text-white">
        <div className="absolute inset-0 overflow-hidden">
          <img
            src={service.image}
            alt={service.title}
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900/90 to-slate-900/70"></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
          <Link
            to="/"
            className="inline-flex items-center text-slate-300 hover:text-white mb-8 transition-colors"
            data-testid="back-button"
          >
            <ArrowLeft className="mr-2 h-4 w-4" /> Back to Home
          </Link>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-5xl md:text-6xl font-bold mb-6" data-testid="service-title">
              {service.title}
            </h1>
            <p className="text-xl md:text-2xl text-slate-300 max-w-3xl">
              {service.description}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Overview Section */}
      <section className="py-16 md:py-24 bg-white" data-testid="overview-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-semibold text-slate-900 mb-6">Service Overview</h2>
              <p className="text-lg text-slate-600 leading-relaxed">
                {service.overview}
              </p>
              <button
                onClick={openLeadForm}
                className="mt-8 bg-slate-900 text-white hover:bg-slate-800 px-8 py-4 font-serif text-lg transition-all hover:tracking-wider shadow-lg hover:shadow-xl"
                data-testid="overview-cta-button"
              >
                Get Started
              </button>
            </div>
            <div>
              <img
                src={service.image2 || 'https://images.unsplash.com/photo-1565688527174-775059ac429c?crop=entropy&cs=srgb&fm=jpg&q=85'}
                alt={`${service.title} consultation`}
                className="w-full h-[400px] object-cover shadow-xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-16 md:py-24 bg-slate-50" data-testid="process-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-semibold text-slate-900 mb-4">Our Legal Process</h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              A structured approach to achieving your legal objectives
            </p>
          </div>

          <div className="space-y-6">
            {service.process.map((item, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="bg-white border-l-4 border-amber-700 p-8 shadow-sm flex gap-6"
                data-testid={`process-step-${idx}`}
              >
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-slate-900 text-white flex items-center justify-center text-xl font-bold">
                    {idx + 1}
                  </div>
                </div>
                <div>
                  <h3 className="text-2xl font-medium text-slate-900 mb-2">{item.step}</h3>
                  <p className="text-slate-600">{item.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 md:py-24 bg-white" data-testid="benefits-section">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-semibold text-slate-900 mb-4">Benefits</h2>
            <p className="text-lg text-slate-600">
              Why choose our {service.title.toLowerCase()} services
            </p>
          </div>

          {/* Mobile: Horizontal scroll, Desktop: Grid */}
          <div className="md:grid md:grid-cols-2 md:gap-6 flex md:flex-none overflow-x-auto gap-6 pb-4 snap-x snap-mandatory scrollbar-hide -mx-6 px-6 md:mx-0 md:px-0">
            {service.benefits.map((benefit, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="flex items-start gap-4 flex-shrink-0 w-[85vw] md:w-auto snap-center bg-slate-50 p-6 md:p-0 md:bg-transparent"
                data-testid={`benefit-${idx}`}
              >
                <Check className="h-6 w-6 text-amber-700 flex-shrink-0 mt-1" />
                <p className="text-lg text-slate-700">{benefit}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 md:py-24 bg-slate-50" data-testid="service-faq-section">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-semibold text-slate-900 mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-lg text-slate-600">
              Common questions about {service.title.toLowerCase()}
            </p>
          </div>

          <Accordion type="single" collapsible className="space-y-4">
            {service.faqs.map((faq, idx) => (
              <AccordionItem 
                key={idx} 
                value={`item-${idx}`} 
                className="border border-slate-200 px-6 bg-white"
                data-testid={`service-faq-${idx}`}
              >
                <AccordionTrigger className="text-left text-lg font-medium text-slate-900 hover:text-amber-700">
                  {faq.q}
                </AccordionTrigger>
                <AccordionContent className="text-slate-600 leading-relaxed">
                  {faq.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Why Choose Our Service */}
      <section className="py-16 md:py-24 bg-white" data-testid="why-choose-service-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-semibold text-slate-900 mb-4">
              Why Choose Our {service.title} Services
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Experience the difference of working with seasoned legal professionals
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-slate-50 p-8 border-l-4 border-amber-700">
              <h3 className="text-2xl font-medium text-slate-900 mb-4">Proven Expertise</h3>
              <p className="text-slate-600 leading-relaxed">
                Our attorneys bring decades of combined experience in {service.title.toLowerCase()}, 
                successfully representing diverse clients across multiple industries and situations.
              </p>
            </div>
            <div className="bg-slate-50 p-8 border-l-4 border-amber-700">
              <h3 className="text-2xl font-medium text-slate-900 mb-4">Personalized Approach</h3>
              <p className="text-slate-600 leading-relaxed">
                We understand that every client's situation is unique. Our team takes the time to understand 
                your specific needs and develops tailored strategies to achieve your objectives.
              </p>
            </div>
            <div className="bg-slate-50 p-8 border-l-4 border-amber-700">
              <h3 className="text-2xl font-medium text-slate-900 mb-4">Transparent Communication</h3>
              <p className="text-slate-600 leading-relaxed">
                Stay informed every step of the way with regular updates, clear explanations of complex legal 
                matters, and straightforward billing practices.
              </p>
            </div>
            <div className="bg-slate-50 p-8 border-l-4 border-amber-700">
              <h3 className="text-2xl font-medium text-slate-900 mb-4">Results-Driven</h3>
              <p className="text-slate-600 leading-relaxed">
                We focus on achieving the best possible outcomes for our clients through strategic planning, 
                meticulous execution, and unwavering dedication to your success.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Client Success Stories */}
      <section className="py-16 md:py-24 bg-slate-50" data-testid="success-stories-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-semibold text-slate-900 mb-4">Our Commitment to Excellence</h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Delivering exceptional legal services and results
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-white p-8 shadow-sm border border-slate-100"
            >
              <div className="text-4xl font-bold text-amber-700 mb-4">Excellence</div>
              <h3 className="text-xl font-medium text-slate-900 mb-2">Quality Service</h3>
              <p className="text-slate-600">
                Committed to delivering the highest quality legal representation and counsel to every client.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="bg-white p-8 shadow-sm border border-slate-100"
            >
              <div className="text-4xl font-bold text-amber-700 mb-4">Expertise</div>
              <h3 className="text-xl font-medium text-slate-900 mb-2">Experienced Team</h3>
              <p className="text-slate-600">
                Our attorneys bring decades of combined experience across all areas of practice.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="bg-white p-8 shadow-sm border border-slate-100"
            >
              <div className="text-4xl font-bold text-amber-700 mb-4">24/7</div>
              <h3 className="text-xl font-medium text-slate-900 mb-2">Client Support</h3>
              <p className="text-slate-600">
                Available around the clock for urgent matters and time-sensitive legal issues.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Industry Experience */}
      <section className="py-16 md:py-24 bg-white" data-testid="industry-experience-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl font-semibold text-slate-900 mb-6">Industry-Specific Expertise</h2>
              <p className="text-lg text-slate-600 leading-relaxed mb-6">
                Our attorneys have deep experience across multiple industries, allowing us to provide informed 
                counsel that considers your specific business context and challenges.
              </p>
              <div className="grid grid-cols-2 gap-4">
                {[
                  'Technology & Software',
                  'Healthcare & Life Sciences',
                  'Financial Services',
                  'Manufacturing',
                  'Professional Services',
                  'Retail & E-commerce',
                  'Real Estate',
                  'Non-Profit Organizations'
                ].map((industry, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <Check className="h-5 w-5 text-amber-700 flex-shrink-0" />
                    <span className="text-slate-700">{industry}</span>
                  </div>
                ))}
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <img
                src="https://images.unsplash.com/photo-1631509057887-7ef19853bc39?crop=entropy&cs=srgb&fm=jpg&q=85"
                alt="Industry expertise"
                className="w-full h-[400px] object-cover shadow-xl"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* What to Expect */}
      <section className="py-16 md:py-24 bg-slate-50" data-testid="what-to-expect-section">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-semibold text-slate-900 mb-4">What to Expect When Working With Us</h2>
            <p className="text-lg text-slate-600">
              A transparent, collaborative process designed for your success
            </p>
          </div>

          <div className="space-y-6">
            <div className="bg-white p-6 border-l-4 border-amber-700 shadow-sm">
              <h3 className="text-xl font-semibold text-slate-900 mb-2">Initial Consultation</h3>
              <p className="text-slate-600">
                We begin with a comprehensive consultation to understand your needs, objectives, and any time 
                constraints. This allows us to provide an accurate assessment and proposed strategy.
              </p>
            </div>

            <div className="bg-white p-6 border-l-4 border-amber-700 shadow-sm">
              <h3 className="text-xl font-semibold text-slate-900 mb-2">Clear Fee Structure</h3>
              <p className="text-slate-600">
                Before beginning work, we provide a detailed engagement letter outlining scope of services, fee 
                structure, and payment terms. No surprises or hidden costs.
              </p>
            </div>

            <div className="bg-white p-6 border-l-4 border-amber-700 shadow-sm">
              <h3 className="text-xl font-semibold text-slate-900 mb-2">Regular Communication</h3>
              <p className="text-slate-600">
                Stay informed with regular status updates, prompt responses to inquiries, and proactive 
                communication about any developments or decisions that require your input.
              </p>
            </div>

            <div className="bg-white p-6 border-l-4 border-amber-700 shadow-sm">
              <h3 className="text-xl font-semibold text-slate-900 mb-2">Dedicated Support</h3>
              <p className="text-slate-600">
                Access to your attorney and our support team throughout the engagement, ensuring you have the 
                guidance you need when you need it.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-slate-900 text-white" data-testid="service-cta-section">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">Ready to Get Started?</h2>
          <p className="text-xl text-slate-300 mb-8">
            Contact us today for a consultation with our experienced attorneys.
          </p>
          <button
            onClick={openLeadForm}
            className="bg-amber-700 text-white hover:bg-amber-800 px-8 py-4 font-serif text-lg transition-colors shadow-lg hover:shadow-xl"
            data-testid="service-bottom-cta-button"
          >
            Schedule Consultation
          </button>
        </div>
      </section>
    </div>
  );
};

export default ServiceDetailPage;
